import React, { useState, useRef } from 'react';
import { useJsApiLoader, Autocomplete, GoogleMap, Marker } from '@react-google-maps/api';
import "./ValidationForm.css";

// Definimos las librerías que necesitamos cargar
const libraries = ['places'];

const ValidatedAddressInput = ({ label, googleApiKey }) => {
    
    // --- Carga del Script de Google ---
    const { isLoaded } = useJsApiLoader({
        googleMapsApiKey: googleApiKey, // Tu API Key de Google Cloud
        libraries: libraries
    });

    // --- Estados ---
    const [address, setAddress] = useState("");
    const [status, setStatus] = useState("idle"); // idle, success
    const [dataSummary, setDataSummary] = useState(null);
    const [coordinates, setCoordinates] = useState(null); // { lat, lng }
    const [showDetails, setShowDetails] = useState(false);

    // Referencia al objeto Autocomplete de Google
    const autocompleteRef = useRef(null);

    // Cuando el mapa carga
    const onLoad = (autocomplete) => {
        autocompleteRef.current = autocomplete;
    };

    // Cuando el usuario selecciona una dirección de la lista
    const onPlaceChanged = () => {
        if (autocompleteRef.current !== null) {
            const place = autocompleteRef.current.getPlace();
            
            if (!place.geometry || !place.geometry.location) {
                // El usuario escribió algo y dio enter sin seleccionar de la lista
                setStatus("error");
                return;
            }

            // 1. Extraer Coordenadas
            const lat = place.geometry.location.lat();
            const lng = place.geometry.location.lng();
            setCoordinates({ lat, lng });

            // 2. Extraer componentes (CP, Ciudad, etc)
            // Google devuelve un array raro, hay que parsearlo:
            const getComponent = (type) => {
                const item = place.address_components.find(c => c.types.includes(type));
                return item ? item.long_name : "";
            };

            const extractedData = {
                "Dirección Completa": place.formatted_address,
                "Calle": getComponent("route"),
                "Número": getComponent("street_number"),
                "Colonia": getComponent("sublocality") || getComponent("neighborhood"),
                "Ciudad/Municipio": getComponent("locality") || getComponent("administrative_area_level_2"),
                "Estado": getComponent("administrative_area_level_1"),
                "Código Postal": getComponent("postal_code"),
                "Coordenadas": `${lat.toFixed(6)}, ${lng.toFixed(6)}`
            };

            setDataSummary(extractedData);
            setAddress(place.formatted_address); // Actualizamos el input con el texto bonito
            setStatus("success");
            setShowDetails(true); // Abrimos detalles automáticamente para mostrar el mapa
        }
    };

    if (!isLoaded) {
        return <div className="form_step_group"><div className="spinner"></div> Cargando Google Maps...</div>;
    }

    return (
        <div className="form_step_group">
            <label className="step_label">{label}</label>
            
            <div className="input_button_wrapper">
                <div className="step_input_container">
                    <Autocomplete
                        onLoad={onLoad}
                        onPlaceChanged={onPlaceChanged}
                    >
                        <input 
                            type="text" 
                            className={`custom_input ${status === 'success' ? 'input_success' : ''}`}
                            placeholder="Escribe la dirección..."
                            // No usamos value={address} controlado estrictamente porque Google maneja el input interno
                            // Usamos defaultValue o dejamos que Google lo gestione
                        />
                    </Autocomplete>
                    
                    <div className="step_status_icon">
                        {status === 'success' && <div className="check_mark">✔</div>}
                    </div>
                </div>

                {/* Botón Reset (opcional) */}
                {status === 'success' && (
                     <button 
                        className="btn_ghost"
                        onClick={() => setShowDetails(!showDetails)}
                    >
                        {showDetails ? 'Ocultar Mapa' : 'Ver Mapa'}
                    </button>
                )}
            </div>

            {/* FICHA TÉCNICA + MAPA */}
            {status === 'success' && showDetails && dataSummary && (
                <div className="data_details_card animate_fade_in">
                    <div className="details_header">
                        Geolocalización Exitosa
                    </div>
                    
                    {/* EL MAPA VISUAL */}
                    <div style={{ height: '200px', width: '100%' }}>
                        <GoogleMap
                            center={coordinates}
                            zoom={15}
                            mapContainerStyle={{ width: '100%', height: '100%' }}
                            options={{
                                disableDefaultUI: true, // Mapa limpio
                                zoomControl: true,
                            }}
                        >
                            {coordinates && <Marker position={coordinates} />}
                        </GoogleMap>
                    </div>

                    <div className="details_grid">
                        {Object.entries(dataSummary).map(([key, val]) => (
                            <div key={key} className="detail_item">
                                <span className="detail_label">{key}:</span>
                                <span className="detail_value">{val || "N/A"}</span>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export default ValidatedAddressInput;